import React from "react";
import { Box, Typography, Divider } from "@mui/material";
import moment from "moment";
import { formatReceiptOrderNumber } from "../../utils/orderNumberFormat";

/**
 * KitchenBill Component
 * Prints kitchen order ticket with all necessary details for food preparation
 */
const KitchenBill = ({ orderResult, companyInfo, paperSize = "80mm" }) => {
  if (!orderResult) return null;

  const formatDate = (dateString) => {
    return moment(dateString).format("MMM DD, YYYY hh:mm A");
  };

  const displayOrderNumber = formatReceiptOrderNumber(
    orderResult.orderNumber,
    orderResult.id,
  );

  return (
    <Box
      sx={{
        width: paperSize,
        fontFamily: "monospace",
        fontSize: "12px",
        p: 2,
        bgcolor: "white",
        color: "black",
        "@media print": {
          width: paperSize,
        },
      }}
    >
      <style>{`
        @page {
          size: ${paperSize} auto;
          margin: 0;
        }
        @media print {
          body { margin: 0; }
        }
      `}</style>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            textTransform: "uppercase",
            mb: 0.5,
          }}
        >
          === KITCHEN ORDER ===
        </Typography>
        {companyInfo?.name && (
          <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
            {companyInfo.name}
          </Typography>
        )}
      </Box>

      <Divider sx={{ borderStyle: "dashed", borderWidth: 1, my: 1.5 }} />

      {/* Order Info */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            textAlign: "center",
            py: 1,
            mb: 1,
            border: "2px solid black",
          }}
        >
          <Typography sx={{ fontSize: "9px", fontWeight: 700, letterSpacing: 1 }}>
            ORDER NUMBER
          </Typography>
          <Typography sx={{ fontSize: "24px", fontWeight: 900, lineHeight: 1.1 }}>
            {displayOrderNumber}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "11px" }}>
          Date: {formatDate(orderResult.createdAt)}
        </Typography>
        <Typography
          sx={{
            fontSize: "11px",
            textTransform: "uppercase",
            fontWeight: 700,
            mt: 0.5,
          }}
        >
          Type: {orderResult.orderType?.replace("-", " ")}
        </Typography>

        {orderResult.Table && (
          <Typography sx={{ fontSize: "14px", fontWeight: 700, mt: 0.5 }}>
            TABLE: {orderResult.Table.tableNumber}
          </Typography>
        )}

        {orderResult.customerName && (
          <Typography sx={{ fontSize: "11px", mt: 0.5 }}>
            Customer: {orderResult.customerName}
          </Typography>
        )}

        {/* Order-level Special Instructions */}
        {orderResult.specialInstructions && (
          <Box
            sx={{
              mt: 1,
              p: 1,
              bgcolor: "#fff3cd",
              borderRadius: 1,
              border: "2px solid #ffc107",
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                mb: 0.5,
                textTransform: "uppercase",
              }}
            >
              ⚠ SPECIAL INSTRUCTIONS:
            </Typography>
            <Typography sx={{ fontSize: "11px", fontWeight: 600 }}>
              {orderResult.specialInstructions}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ borderStyle: "dashed", borderWidth: 1, my: 1.5 }} />

      {/* Order Items */}
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 700,
            mb: 1,
            textTransform: "uppercase",
          }}
        >
          ITEMS TO PREPARE:
        </Typography>

        {orderResult.OrderItems?.map((orderItem, index) => (
          <Box
            key={index}
            sx={{ mb: 2, pb: 1.5, borderBottom: "1px dashed #ccc" }}
          >
            {/* Item Quantity and Name */}
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
            >
              <Typography sx={{ fontSize: "13px", fontWeight: 900 }}>
                {orderItem.quantity}x
              </Typography>
              <Typography
                sx={{ fontSize: "13px", fontWeight: 700, flex: 1, ml: 1 }}
              >
                {orderItem.MenuItem?.name ||
                  orderItem.Offer?.name ||
                  "Unknown Item"}
              </Typography>
            </Box>

            {/* Variation */}
            {orderItem.ItemVariation && (
              <Typography sx={{ fontSize: "11px", ml: 3, fontStyle: "italic" }}>
                → {orderItem.ItemVariation.name}
              </Typography>
            )}

            {/* Modifiers */}
            {orderItem.OrderItemModifiers &&
              orderItem.OrderItemModifiers.length > 0 && (
                <Box sx={{ ml: 3, mt: 0.5 }}>
                  {orderItem.OrderItemModifiers.map((mod, modIndex) => (
                    <Typography key={modIndex} sx={{ fontSize: "11px" }}>
                      + {mod.ModifierOption?.name || "Modifier"}
                    </Typography>
                  ))}
                </Box>
              )}

            {/* Special Instructions */}
            {orderItem.specialInstructions && (
              <Box
                sx={{
                  ml: 3,
                  mt: 0.5,
                  p: 0.5,
                  bgcolor: "#f5f5f5",
                  borderRadius: 0.5,
                  border: "1px solid #ddd",
                }}
              >
                <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>
                  ⚠ NOTE: {orderItem.specialInstructions}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <Divider sx={{ borderStyle: "dashed", borderWidth: 1, my: 1.5 }} />

      {/* Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{ fontSize: "13px", fontWeight: 700, textTransform: "uppercase" }}
        >
          Total Items:{" "}
          {orderResult.OrderItems?.reduce(
            (sum, item) => sum + item.quantity,
            0,
          ) || 0}
        </Typography>
      </Box>

      <Divider sx={{ borderStyle: "dashed", borderWidth: 1, my: 1.5 }} />

      {/* Footer */}
      <Box sx={{ textAlign: "center", mt: 2 }}>
        <Typography sx={{ fontSize: "12px", fontWeight: 900 }}>
          PREPARE WITH CARE
        </Typography>
        <Typography sx={{ fontSize: "10px", mt: 0.5 }}>
          Printed: {moment().format("MMM DD, YYYY hh:mm A")}
        </Typography>
      </Box>
    </Box>
  );
};

export default KitchenBill;
